import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, CalendarDays, CheckSquare, Images, Link as LinkIcon, Loader2, PlaySquare, Plus, SquareStack, Trash2 } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { EventDatePickerDialog } from '../components/EventDatePickerDialog';
import { PhotographerOnboardingGuide } from '../components/PhotographerOnboardingGuide';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/Dialog';
import { Button } from '../components/ui/Button';
import { Field, Input, Textarea } from '../components/ui/Field';
import {
  archiveGallery,
  completeOnboarding,
  createGallery,
  deleteGallery,
  listGalleries,
  listPublicSubscriptionPlans,
  type Gallery,
  type NotificationPreferences,
  type Photographer,
  type BillingInterval,
  type SubscriptionPlanId,
  type SubscriptionView,
  type UpdateProfileInput
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateOnlyLong } from '../lib/dateOnly';
import { handleMediaError, handleMediaLoad, useMediaRecovery, type MediaErrorHandler } from '../lib/mediaRecovery';
import { absoluteGalleryUrl, slugify, slugInputValue } from '../lib/utils';

export function DashboardPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [selectedGalleryIds, setSelectedGalleryIds] = useState<Set<string>>(() => new Set());
  const [shareGallerySlug, setShareGallerySlug] = useState<string | null>(null);
  const recoverGalleryMedia = useMediaRecovery([['galleries']]);

  const galleriesQuery = useQuery({
    queryKey: ['galleries'],
    queryFn: () => listGalleries(token!),
    enabled: Boolean(token)
  });

  const createMutation = useMutation({
    mutationFn: (input: { title: string; slug?: string; description?: string; eventDate?: string | null; passcode: string }) => createGallery(token!, input),
    onSuccess: ({ gallery }) => {
      setCreateOpen(false);
      void navigate(`/galleries/${gallery.galleryId}`);
    }
  });

  const archiveMutation = useMutation({
    mutationFn: async (galleryIds: string[]) => {
      await Promise.all(galleryIds.map((galleryId) => archiveGallery(token!, galleryId)));
      return galleryIds;
    },
    onSuccess: () => {
      setSelectedGalleryIds(new Set());
      setManageOpen(false);
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (galleryIds: string[]) => {
      await Promise.all(galleryIds.map((galleryId) => deleteGallery(token!, galleryId)));
      return galleryIds;
    },
    onSuccess: () => {
      setSelectedGalleryIds(new Set());
      setManageOpen(false);
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
    }
  });


  const onboardingMutation = useMutation({
    mutationFn: () => completeOnboarding(token!),
    onSuccess: ({ photographer }) => {
      queryClient.setQueryData<Awaited<ReturnType<typeof listGalleries>>>(['galleries'], (current) => current ? ({
        ...current,
        photographer
      }) : current);
    }
  });

  if (!galleriesQuery.data) {
    return (
      <AppShell>
        {galleriesQuery.isError ? (
          <DashboardErrorState onRetry={() => void galleriesQuery.refetch()} />
        ) : (
          <DashboardLoadingState />
        )}
      </AppShell>
    );
  }

  const galleries = galleriesQuery.data.galleries;
  const sortedGalleries = [...galleries].sort((left, right) => {
    if (left.isArchived !== right.isArchived) {
      return left.isArchived ? 1 : -1;
    }
    return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
  });
  const imageCount = galleries.reduce((total, gallery) => total + imageCountOf(gallery), 0);
  const videoCount = galleries.reduce((total, gallery) => total + (gallery.videoCount ?? 0), 0);
  const mediaCount = galleries.reduce((total, gallery) => total + gallery.photoCount, 0);
  const photographer = galleriesQuery.data.photographer;
  const shareUrl = shareGallerySlug ? absoluteGalleryUrl(photographer.slug, shareGallerySlug) : null;
  const welcomeName = photographer.displayName || photographer.email;
  const selectedGalleries = galleries.filter((gallery) => selectedGalleryIds.has(gallery.galleryId));
  const manageBusy = archiveMutation.isPending || deleteMutation.isPending;

  return (
    <AppShell>
      <PhotographerOnboardingGuide
        open={photographer.showOnboardingGuide}
        isPending={onboardingMutation.isPending}
        error={onboardingMutation.error}
        onDismiss={() => {
          if (!onboardingMutation.isPending) onboardingMutation.mutate();
        }}
      />
      <div className="grid gap-6">
        <h1 className="text-2xl font-medium text-ink sm:text-3xl">Welcome, {welcomeName}</h1>

        <section className="relative overflow-hidden rounded-sm bg-ink p-6 text-white shadow-soft">
          <div className="relative z-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-medium text-white/60">Storyly Dashboard</p>
              <h2 className="mt-2 text-3xl font-semibold leading-tight sm:text-4xl">Client galleries</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              <Dialog open={manageOpen} onOpenChange={setManageOpen}>
                <DialogTrigger asChild>
                  <Button variant="secondary" type="button">
                    <SquareStack className="h-4 w-4" />
                    <span>Manage Galleries</span>
                  </Button>
                </DialogTrigger>
                <DialogContent
                  title="Manage galleries"
                  description="Select one or more galleries, then make them inactive or permanently delete them."
                  className="max-w-3xl"
                >
                  <ManageGalleriesPanel
                    galleries={sortedGalleries}
                    selectedGalleryIds={selectedGalleryIds}
                    selectedGalleries={selectedGalleries}
                    isBusy={manageBusy}
                    archiveError={archiveMutation.error}
                    deleteError={deleteMutation.error}
                    onToggle={(galleryId) =>
                      setSelectedGalleryIds((current) => {
                        const next = new Set(current);
                        if (next.has(galleryId)) {
                          next.delete(galleryId);
                        } else {
                          next.add(galleryId);
                        }
                        return next;
                      })
                    }
                    onSelectAll={() => setSelectedGalleryIds(new Set(sortedGalleries.map((gallery) => gallery.galleryId)))}
                    onClear={() => setSelectedGalleryIds(new Set())}
                    onArchive={() => archiveMutation.mutate([...selectedGalleryIds])}
                    onDelete={() => deleteMutation.mutate([...selectedGalleryIds])}
                    onMediaError={recoverGalleryMedia}
                  />
                </DialogContent>
              </Dialog>
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button variant="secondary">
                    <Plus className="h-4 w-4" />
                    <span>New gallery</span>
                  </Button>
                </DialogTrigger>
                <DialogContent title="Create gallery" description="Choose a passcode for this client gallery.">
                  <CreateGalleryForm
                    isPending={createMutation.isPending}
                    error={createMutation.error}
                    onSubmit={createMutation.mutate}
                  />
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Metric icon={<Images className="h-4 w-4" />} label="Galleries" value={String(galleries.length)} />
          <Metric icon={<Images className="h-4 w-4" />} label="Photos" value={String(imageCount)} />
          <Metric icon={<PlaySquare className="h-4 w-4" />} label="Videos" value={String(videoCount)} />
          <Metric icon={<SquareStack className="h-4 w-4" />} label="Stored media" value={`${mediaCount} total`} />
        </section>

        {shareUrl ? (
          <div className="flex flex-col gap-3 rounded-sm border border-sea/20 bg-sea/10 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="min-w-0 truncate text-sm font-medium text-ink">{shareUrl}</p>
            <Button variant="secondary" size="sm" onClick={() => void navigator.clipboard.writeText(shareUrl)}>
              <LinkIcon className="h-4 w-4" />
              <span>Copy</span>
            </Button>
          </div>
        ) : null}

        <section className="grid gap-7 rounded-sm bg-white/70 p-6 shadow-sm sm:grid-cols-2 lg:grid-cols-3">
          {galleriesQuery.isLoading ? <SkeletonRows /> : null}
          {sortedGalleries.map((gallery) => (
            <GalleryCard key={gallery.galleryId} gallery={gallery} onMediaError={recoverGalleryMedia} />
          ))}
          {!galleriesQuery.isLoading && galleries.length === 0 ? (
            <div className="rounded-sm border border-dashed border-ink/20 bg-white/70 p-8 text-center sm:col-span-2 lg:col-span-3">
              <p className="font-medium text-ink">No galleries yet</p>
              <p className="mt-1 text-sm text-ink/70">Create one to start uploading photos and videos.</p>
            </div>
          ) : null}
        </section>
      </div>
    </AppShell>
  );
}

function DashboardLoadingState() {
  return (
    <div className="grid gap-6" aria-label="Loading dashboard">
      <div className="flex min-h-9 items-center gap-3 text-ink">
        <Loader2 className="h-6 w-6 animate-spin text-sea" aria-hidden="true" />
        <p className="text-2xl font-medium sm:text-3xl">Loading dashboard</p>
      </div>

      <section className="relative overflow-hidden rounded-sm bg-ink p-6 text-white shadow-soft">
        <div className="relative z-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <p className="text-sm font-medium text-white/60">Storyly Dashboard</p>
            <h2 className="mt-2 text-3xl font-semibold leading-tight sm:text-4xl">Client galleries</h2>
          </div>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="h-36 animate-pulse rounded-sm bg-white shadow-sm" />
        <div className="h-36 animate-pulse rounded-sm bg-white shadow-sm" />
        <div className="h-36 animate-pulse rounded-sm bg-white shadow-sm" />
      </section>

      <section className="grid gap-7 rounded-sm bg-white/70 p-6 shadow-sm sm:grid-cols-2 lg:grid-cols-3">
        <SkeletonRows />
      </section>
    </div>
  );
}

function DashboardErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="grid gap-6" aria-label="Dashboard unavailable">
      <section className="relative overflow-hidden rounded-sm bg-ink p-6 text-white shadow-soft">
        <div className="relative z-10 max-w-2xl">
          <p className="text-sm font-medium text-white/60">Storyly Dashboard</p>
          <h2 className="mt-2 text-3xl font-semibold leading-tight sm:text-4xl">Client galleries</h2>
        </div>
      </section>

      <div className="rounded-sm border border-ink/10 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-ink">Could not load dashboard</h1>
        <p className="mt-2 text-sm text-ink/70">Refresh the dashboard data or sign in again if the session expired.</p>
        <div className="mt-4">
          <Button type="button" onClick={onRetry}>
            <Loader2 className="h-4 w-4" />
            <span>Retry</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

function ManageGalleriesPanel({
  galleries,
  selectedGalleryIds,
  selectedGalleries,
  isBusy,
  archiveError,
  deleteError,
  onToggle,
  onSelectAll,
  onClear,
  onArchive,
  onDelete,
  onMediaError
}: {
  galleries: Gallery[];
  selectedGalleryIds: Set<string>;
  selectedGalleries: Gallery[];
  isBusy: boolean;
  archiveError: Error | null;
  deleteError: Error | null;
  onToggle: (galleryId: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onMediaError?: MediaErrorHandler;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const selectedCount = selectedGalleryIds.size;
  const hasSelection = selectedCount > 0;

  function handleDelete() {
    if (!confirmDelete) {
      return;
    }
    onDelete();
    setConfirmDelete(false);
  }

  return (
    <div className="grid gap-5">
      <div className="flex flex-col gap-3 rounded-sm border border-ink/10 bg-white/65 p-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-medium text-ink">{selectedCount} selected</p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={onSelectAll} disabled={galleries.length === 0 || isBusy}>
            <CheckSquare className="h-4 w-4" />
            <span>Select all</span>
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onClear} disabled={!hasSelection || isBusy}>
            Clear selection
          </Button>
        </div>
      </div>

      <div className="max-h-[42vh] overflow-auto rounded-sm border border-ink/10 bg-white">
        {galleries.length > 0 ? (
          galleries.map((gallery) => (
            <label key={gallery.galleryId} className="flex cursor-pointer items-center gap-3 border-b border-ink/10 px-3 py-3 last:border-b-0">
              <input
                type="checkbox"
                className="h-4 w-4 accent-ink"
                checked={selectedGalleryIds.has(gallery.galleryId)}
                onChange={() => onToggle(gallery.galleryId)}
                disabled={isBusy}
              />
              <img
                src={gallery.coverUrl ?? '/brand/gallery-placeholder.jpg'}
                alt=""
                className="h-14 w-16 rounded-sm object-cover"
                loading="lazy"
                onLoad={handleMediaLoad}
                onError={(event) => gallery.coverUrl && handleMediaError(event, onMediaError)}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-ink">{gallery.title}</span>
                <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink/70">
                  <span>{imageCountOf(gallery)} photos</span>
                  <span>{gallery.videoCount ?? 0} videos</span>
                  {gallery.isArchived ? <span className="rounded-full bg-clay/10 px-2 py-0.5 font-medium text-clay">Inactive</span> : <span>Active</span>}
                </span>
              </span>
            </label>
          ))
        ) : (
          <div className="p-6 text-center text-sm text-ink/70">No galleries to manage.</div>
        )}
      </div>

      <div className="grid gap-3 rounded-sm border border-ink/10 bg-white/65 p-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-ink">Make inactive</p>
            <p className="mt-1 text-sm text-ink/70">Inactive galleries stay editable here, but client links stop working.</p>
          </div>
          <Button type="button" variant="secondary" onClick={onArchive} disabled={!hasSelection || isBusy}>
            <Archive className="h-4 w-4" />
            <span>{isBusy ? 'Working' : 'Make inactive'}</span>
          </Button>
        </div>
        {archiveError ? <p className="text-sm text-clay">{archiveError.message}</p> : null}
      </div>

      <div className="grid gap-3 rounded-sm border border-clay/30 bg-clay/10 p-3">
        <div>
          <p className="text-sm font-semibold text-clay">Permanent delete</p>
          <p className="mt-1 text-sm text-ink/70">
            This permanently deletes the selected galleries, media records, export records, and all gallery media files. This cannot be undone.
          </p>
          {selectedGalleries.length > 0 ? (
            <p className="mt-2 text-xs text-ink/70">
              Selected: {selectedGalleries.map((gallery) => gallery.title).join(', ')}
            </p>
          ) : null}
        </div>
        <label className="flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-clay"
            checked={confirmDelete}
            onChange={(event) => setConfirmDelete(event.target.checked)}
            disabled={!hasSelection || isBusy}
          />
          <span>I understand this permanently deletes all selected gallery data and files.</span>
        </label>
        <div className="flex justify-end">
          <Button type="button" variant="danger" onClick={handleDelete} disabled={!hasSelection || !confirmDelete || isBusy}>
            <Trash2 className="h-4 w-4" />
            <span>{isBusy ? 'Deleting' : 'Delete selected permanently'}</span>
          </Button>
        </div>
        {deleteError ? <p className="text-sm text-clay">{deleteError.message}</p> : null}
      </div>
    </div>
  );
}

const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  favouriteSelections: true,
  galleryDownloads: true
};

export function DashboardSettingsForm({
  photographer,
  subscription,
  plans,
  subscriptionLoading,
  subscriptionRequestPending,
  subscriptionRequestError,
  onRequestSubscription,
  isPending,
  error,
  identitySyncRequired,
  onSubmit
}: {
  photographer: Photographer;
  subscription?: SubscriptionView;
  plans: Awaited<ReturnType<typeof listPublicSubscriptionPlans>>['plans'];
  subscriptionLoading: boolean;
  subscriptionRequestPending: boolean;
  subscriptionRequestError: Error | null;
  onRequestSubscription: (input: { targetPlanId: Exclude<SubscriptionPlanId, 'trial'>; billingInterval: BillingInterval; note?: string }) => void;
  isPending: boolean;
  error: Error | null;
  identitySyncRequired: boolean;
  onSubmit: (input: UpdateProfileInput) => void;
}) {
  const initialPreferences = photographer.notificationPreferences ?? DEFAULT_NOTIFICATION_PREFERENCES;
  const [displayName, setDisplayName] = useState(photographer.displayName);
  const [slug, setSlug] = useState(photographer.slug);
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences>(initialPreferences);
  const [requestedPlanId, setRequestedPlanId] = useState<Exclude<SubscriptionPlanId, 'trial'>>('essential');
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');
  const [subscriptionNote, setSubscriptionNote] = useState('');
  const trimmedDisplayName = displayName.trim();
  const slugIsValid = slug.length >= 2 && slug.length <= 80 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
  const isDirty =
    trimmedDisplayName !== photographer.displayName ||
    slug !== photographer.slug ||
    notificationPreferences.favouriteSelections !== initialPreferences.favouriteSelections ||
    notificationPreferences.galleryDownloads !== initialPreferences.galleryDownloads;
  const canSave = (isDirty || identitySyncRequired) && Boolean(trimmedDisplayName) && trimmedDisplayName.length <= 100 && slugIsValid && !isPending;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canSave) return;
    onSubmit({ displayName: trimmedDisplayName, slug, notificationPreferences });
  }

  return (
    <form className="grid gap-6" onSubmit={handleSubmit}>
      <section className="grid gap-4" aria-labelledby="profile-settings-heading">
        <div>
          <h3 id="profile-settings-heading" className="text-base font-semibold text-ink">Profile</h3>
          <p className="mt-1 text-sm text-ink/70">Your business name appears on your dashboard and public Storyly pages.</p>
        </div>
        <div className="grid items-start gap-4 sm:grid-cols-2">
          <Field label="Business or display name">
            <Input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              maxLength={100}
              required
              autoComplete="organization"
            />
          </Field>
          <Field label="Sign-in email" hint="Managed by your Storyly sign-in account">
            <Input value={photographer.email} readOnly aria-readonly="true" className="cursor-not-allowed bg-smoke text-ink/65" />
          </Field>
        </div>
        <Field
          label="Public slug"
          hint={slugIsValid ? 'Use 2–80 lowercase letters, numbers, and single hyphens.' : 'Enter 2–80 lowercase letters, numbers, and single hyphens.'}
        >
          <Input
            value={slug}
            onChange={(event) => setSlug(slugInputValue(event.target.value).slice(0, 80))}
            minLength={2}
            maxLength={80}
            required
            aria-invalid={!slugIsValid}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </Field>
        <div className="rounded-sm border border-ink/10 bg-white/70 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink/55">Gallery URL preview</p>
          <p className="mt-1 break-all text-sm text-ink" aria-live="polite">
            {window.location.origin}/g/{slug || 'your-slug'}/…
          </p>
        </div>
      </section>

      <section className="grid gap-3 border-t border-ink/10 pt-5" aria-labelledby="notification-settings-heading">
        <div>
          <h3 id="notification-settings-heading" className="text-base font-semibold text-ink">Email notifications</h3>
          <p className="mt-1 text-sm text-ink/70">Choose which client activity emails Storyly sends you.</p>
        </div>
        <NotificationSwitch
          label="Favourite selection emails"
          description="Email me when a client submits a favourite selection."
          checked={notificationPreferences.favouriteSelections}
          onCheckedChange={(favouriteSelections) => setNotificationPreferences((current) => ({ ...current, favouriteSelections }))}
        />
        <NotificationSwitch
          label="Gallery download emails"
          description="Email me when a client has downloaded a gallery."
          checked={notificationPreferences.galleryDownloads}
          onCheckedChange={(galleryDownloads) => setNotificationPreferences((current) => ({ ...current, galleryDownloads }))}
        />
      </section>

      <section className="grid gap-4 border-t border-ink/10 pt-5" aria-labelledby="subscription-settings-heading">
        <div>
          <h3 id="subscription-settings-heading" className="text-base font-semibold text-ink">Subscription</h3>
          <p className="mt-1 text-sm text-ink/70">View your current allowance or ask Storyly to change your plan.</p>
        </div>
        {subscriptionLoading ? <p className="text-sm text-ink/65">Loading subscription…</p> : subscription ? (
          <div className="grid gap-3 rounded-sm border border-ink/10 bg-white/70 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-lg font-semibold">{subscription.plan.name}</p>
                <p className="text-xs uppercase tracking-wide text-ink/55">{subscription.subscription.billingInterval} billing</p>
              </div>
              <span className="rounded-full bg-ink px-3 py-1 text-xs font-semibold text-ivory">Current plan</span>
            </div>
            <div className="grid gap-2 text-sm sm:grid-cols-3">
              <UsageItem label="Storage" value={`${formatSubscriptionBytes(subscription.subscription.storageBytesUsed)} / ${formatLimitBytes(subscription.effectiveLimits.storageBytes)}`} />
              <UsageItem label="Active galleries" value={`${subscription.subscription.activeGalleriesUsed} / ${subscription.effectiveLimits.activeGalleries ?? 'Unlimited'}`} />
              <UsageItem label="Photos per gallery" value={subscription.effectiveLimits.photosPerGallery.toLocaleString('en-ZA')} />
            </div>
            {subscription.subscription.pendingChangeRequest ? (
              <p className="rounded-sm bg-clay/10 p-3 text-sm text-clay">
                Request pending: {subscription.subscription.pendingChangeRequest.targetPlanId} ({subscription.subscription.pendingChangeRequest.billingInterval}).
              </p>
            ) : (
              <div className="grid gap-3 border-t border-ink/10 pt-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Requested plan">
                    <select className="h-11 border border-ink/20 bg-white px-3 text-sm" value={requestedPlanId} onChange={(event) => {
                      const value = event.target.value as Exclude<SubscriptionPlanId, 'trial'>;
                      setRequestedPlanId(value);
                      setBillingInterval(value === 'free' ? 'none' : value === 'enterprise' ? 'custom' : 'monthly');
                    }}>
                      {plans.map((plan) => <option key={plan.planId} value={plan.planId}>{plan.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Billing">
                    <select className="h-11 border border-ink/20 bg-white px-3 text-sm" value={billingInterval} disabled={requestedPlanId === 'free' || requestedPlanId === 'enterprise'} onChange={(event) => setBillingInterval(event.target.value as BillingInterval)}>
                      {requestedPlanId === 'free' ? <option value="none">Free</option> : requestedPlanId === 'enterprise' ? <option value="custom">Custom quote</option> : <><option value="monthly">Monthly</option><option value="annual">Annual</option></>}
                    </select>
                  </Field>
                </div>
                <Field label="Note" hint="Optional — tell us what you need.">
                  <Textarea value={subscriptionNote} maxLength={1000} onChange={(event) => setSubscriptionNote(event.target.value)} />
                </Field>
                <div className="flex justify-end">
                  <Button type="button" variant="secondary" disabled={subscriptionRequestPending || requestedPlanId === subscription.subscription.planId} onClick={() => onRequestSubscription({ targetPlanId: requestedPlanId, billingInterval, note: subscriptionNote.trim() || undefined })}>
                    {subscriptionRequestPending ? 'Sending request…' : 'Request plan change'}
                  </Button>
                </div>
              </div>
            )}
            {subscriptionRequestError ? <p role="alert" className="text-sm text-clay">{subscriptionRequestError.message}</p> : null}
          </div>
        ) : <p className="text-sm text-clay">Your subscription is still being configured. Please contact Storyly support.</p>}
      </section>

      {error ? <p role="alert" className="text-sm text-clay">{error.message}</p> : null}
      <div className="flex justify-end">
        <Button type="submit" disabled={!canSave}>
          {isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          <span>{isPending ? 'Saving' : 'Save settings'}</span>
        </Button>
      </div>
    </form>
  );
}

function UsageItem({ label, value }: { label: string; value: string }) {
  return <div className="rounded-sm bg-smoke p-3"><p className="text-xs uppercase tracking-wide text-ink/55">{label}</p><p className="mt-1 font-semibold">{value}</p></div>;
}

function formatSubscriptionBytes(bytes: number): string {
  return `${(bytes / 1_000_000_000).toLocaleString('en-ZA', { maximumFractionDigits: 1 })} GB`;
}

function formatLimitBytes(bytes?: number): string {
  return bytes === undefined ? 'Unlimited' : formatSubscriptionBytes(bytes);
}

function NotificationSwitch({
  label,
  description,
  checked,
  onCheckedChange
}: {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-sm border border-ink/10 bg-white/70 p-3">
      <div>
        <p className="text-sm font-semibold text-ink">{label}</p>
        <p className="mt-1 text-xs text-ink/65">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onCheckedChange(!checked)}
        className={`relative h-7 w-12 shrink-0 rounded-full transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black ${checked ? 'bg-black' : 'bg-ink/25'}`}
      >
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-ivory shadow-sm transition ${checked ? 'left-6' : 'left-1'}`} />
      </button>
    </div>
  );
}

function CreateGalleryForm({
  isPending,
  error,
  onSubmit
}: {
  isPending: boolean;
  error: Error | null;
  onSubmit: (input: { title: string; slug?: string; description?: string; eventDate?: string | null; passcode: string }) => void;
}) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState<string | null>(null);
  const [passcode, setPasscode] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSubmit({ title, slug: slug || slugify(title), description, eventDate, passcode });
  }

  return (
    <>
      <form className="grid gap-4" onSubmit={handleSubmit}>
        <Field label="Title">
          <Input value={title} onChange={(event) => setTitle(event.target.value)} required placeholder="Ryan & Julia" />
        </Field>
        <Field label="Description">
          <Textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="A short note for the gallery cover" />
        </Field>
        <Field label="Event date" hint="Optional">
          <button
            type="button"
            className="flex h-11 w-full items-center justify-between rounded-sm border border-ink/20 bg-ivory px-3.5 text-sm text-ink outline-none transition focus:border-black focus:ring-4 focus:ring-black/10"
            onClick={() => setDatePickerOpen(true)}
          >
            <span className={eventDate ? 'text-ink' : 'text-ink/70'}>{eventDate ? formatDateOnlyLong(eventDate) : 'Select a date'}</span>
            <CalendarDays className="h-4 w-4 text-ink/60" />
          </button>
        </Field>
        <Field label="Passcode">
          <Input value={passcode} onChange={(event) => setPasscode(event.target.value)} required minLength={4} />
        </Field>
        {error ? <p className="text-sm text-clay">{error.message}</p> : null}
        <Button disabled={isPending}>{isPending ? 'Creating' : 'Create'}</Button>
      </form>
      <EventDatePickerDialog open={datePickerOpen} value={eventDate} onOpenChange={setDatePickerOpen} onChange={setEventDate} />
    </>
  );
}

function Metric({
  icon,
  label,
  value,
  hint,
  compact = false
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  compact?: boolean;
}) {
  return (
    <div className="rounded-sm border border-ink/10 bg-white p-4 shadow-sm">
      <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-sm bg-smoke text-ink/70">{icon}</div>
      <p className="text-sm text-ink/70">{label}</p>
      <p className={`mt-1 font-semibold text-ink ${compact ? 'break-all text-base sm:text-lg xl:text-xl' : 'truncate text-2xl'}`}>{value}</p>
      {hint ? <p className={`mt-2 text-xs text-ink/70 ${compact ? 'break-all leading-relaxed' : 'truncate'}`}>{hint}</p> : null}
    </div>
  );
}

function GalleryCard({ gallery, onMediaError }: { gallery: Gallery; onMediaError?: MediaErrorHandler }) {
  return (
    <Link to={`/galleries/${gallery.galleryId}`} className="group block min-w-0 transition hover:-translate-y-0.5">
      <div className="aspect-[4/3] overflow-hidden rounded-sm bg-smoke shadow-sm">
        <img
          src={gallery.coverUrl ?? '/brand/gallery-placeholder.jpg'}
          alt=""
          className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.025]"
          loading="lazy"
          onLoad={handleMediaLoad}
          onError={(event) => gallery.coverUrl && handleMediaError(event, onMediaError)}
        />
      </div>
      <div className="mt-4 min-w-0">
        <h2 className="truncate text-lg font-medium text-ink sm:text-xl">{gallery.title}</h2>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-smoke px-2 py-1 text-xs font-medium text-ink/70">{imageCountOf(gallery)} photos</span>
          <span className="rounded-full bg-smoke px-2 py-1 text-xs font-medium text-ink/70">{gallery.videoCount ?? 0} videos</span>
          {gallery.isArchived ? <span className="rounded-full bg-clay/10 px-2 py-1 text-xs font-medium text-clay">Inactive</span> : null}
        </div>
      </div>
    </Link>
  );
}

function imageCountOf(gallery: Gallery): number {
  return gallery.imageCount ?? Math.max(0, gallery.photoCount - (gallery.videoCount ?? 0));
}

function SkeletonRows() {
  return (
    <>
      <div className="h-80 animate-pulse rounded-sm bg-white/70" />
      <div className="h-80 animate-pulse rounded-sm bg-white/70" />
      <div className="h-80 animate-pulse rounded-sm bg-white/70" />
    </>
  );
}
